import { FC, useEffect } from 'react';
import { Provider, useUnit } from 'effector-react/scope';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
  type AutocompleteProps,
} from '@mui/material';
import { Listbox } from '../../components/listbox';
import { withControl } from '../../hoc/withControl';
import { createScope, $data, $pending, getList as getListEvent } from './model';
import type { Resident, GetList } from './types';

export const ResidentParentListControl: FC<any> = ({
  value,
  setValue,
  error,
  label,
  required,
  readOnly,
  disabled,
  isError,
  helperText,
  areaGuid,
  placeholder,
  ...props
}) => {
  const [data, pending, getList] = useUnit([$data, $pending, getListEvent]);

  useEffect(() => {
    if (areaGuid) {
      getList({ areaGuid });
    }
  }, [areaGuid]);

  const findedValue =
    value !== ''
      ? (data?.find(
          (item: Resident) => item.id === (typeof value === 'object' ? value?.id : value)
        ) ?? null)
      : null;

  const handleChange = (_: unknown, newValue: any) => setValue(newValue || null);

  return (
    <FormControl required={required} disabled={disabled}>
      {label && (
        <FormLabel required={!readOnly && required} error={isError}>
          {label}
        </FormLabel>
      )}
      <Autocomplete
        loading={pending}
        value={findedValue}
        options={!data && !areaGuid ? [] : data}
        onChange={handleChange}
        ListboxComponent={Listbox}
        renderInput={(params): JSX.Element => (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              readOnly,
              error: isError,
              placeholder,
              endAdornment: readOnly ? null : (
                <>
                  {pending && <CircularProgress size={20} color="inherit" />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        readOnly={readOnly}
        disabled={disabled}
        {...props}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
      {helperText && !error && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export const ResidentParentListControlWrapper = withControl<
  Partial<AutocompleteProps<Resident, boolean, boolean, boolean>> &
    GetList & { placeholder?: string }
>(({ name, ...props }) => {
  const scope = createScope(name);

  return (
    <Provider value={scope}>
      <ResidentParentListControl {...props} />
    </Provider>
  );
});
